import localStore from 'store';

/**
 * Frontend SDK embed mode helpers.
 *
 * The portal runs inside an iframe owned by a customer page. The SDK picks a
 * route under /portal/:pid/embed/... and describes how much portal "chrome"
 * to render through the query string:
 *
 *   chrome      full | minimal | none      (default none)
 *   completion  trophy | message | event | collapse   (default event)
 *   message     text shown when completion=message
 *   fit         fill: the host gives none/minimal a fixed height, so the
 *               lesson fills the frame like chrome=full (default content)
 *
 * The contact token travels in the URL fragment (#cy_auth=...), which the
 * browser never sends to the server, and is moved into local storage before
 * the portal logs in.
 */

export const CHROME_LEVELS = ['full', 'minimal', 'none'];
export const COMPLETION_MODES = ['trophy', 'message', 'event', 'collapse'];

const pick = (value, allowed, fallback) =>
  allowed.includes(value) ? value : fallback;

export const isEmbedPath = (path = '') => /\/embed\//.test(path);

export const embedContextFromRoute = (route) => {
  const path = route?.path || '';
  const active = !!route?.meta?.embed || isEmbedPath(path);
  const query = route?.query || {};
  const chrome = active ? pick(query.chrome, CHROME_LEVELS, 'none') : 'full';

  return {
    active,
    chrome,
    completion: active
      ? pick(query.completion, COMPLETION_MODES, 'event')
      : 'trophy',
    message: typeof query.message === 'string' ? query.message : '',
    fit:
      active && chrome !== 'full' && query.fit !== 'fill' ? 'content' : 'fill',
  };
};

/** chrome none/minimal without fit=fill: the iframe is content-sized, not a viewport. */
export const isContentSizedEmbed = (ctx) =>
  !!(ctx && ctx.active && ctx.fit === 'content');

/**
 * Move `#cy_auth=<base64url json>` from the fragment into portal_auth storage
 * and scrub it from the address bar.
 *
 * @returns {{id: string, token: string}|null}
 */
export const consumeEmbedAuth = (win = window) => {
  const hash = win.location.hash || '';
  const match = hash.match(/(?:^#|&)cy_auth=([^&]+)/);
  if (!match) return null;

  let auth = null;
  try {
    const base64 = match[1].replace(/-/g, '+').replace(/_/g, '/');
    auth = JSON.parse(win.atob(base64));
  } catch (e) {
    auth = null;
  }

  const cleaned = hash.replace(match[0], '').replace(/^#&/, '#');
  const url =
    win.location.pathname +
    win.location.search +
    (cleaned === '#' ? '' : cleaned);
  win.history.replaceState(win.history.state, '', url);

  if (!auth || !auth.id || !auth.token) return null;

  localStore.remove('portal_testing_pid');
  localStore.set('portal_auth', { id: auth.id, token: auth.token });
  return auth;
};

/**
 * Full-viewport UI the portal (or a third party widget it loaded) opens over
 * the lesson. Inside a content-sized iframe these would be clipped, so the
 * host is told to expand the frame while any of them is open.
 */
export const OVERLAY_SELECTORS = [
  '.v-dialog__content--active',
  '.v-overlay--active',
  '.v-menu__content--active',
  '.calendly-overlay',
  'cal-modal-box:not([state="closed"])',
  '.file-viewer--active',
];

export const hasActiveOverlay = (doc = document) =>
  OVERLAY_SELECTORS.some((selector) => {
    try {
      return !!doc.querySelector(selector);
    } catch (e) {
      return false;
    }
  });

/**
 * Watch the document for overlay elements and call onChange(active) on every
 * transition. Returns a stop function.
 */
export const observeOverlays = (onChange, doc = document) => {
  let active = false;
  let scheduled = false;

  const check = () => {
    scheduled = false;
    const next = hasActiveOverlay(doc);
    if (next !== active) {
      active = next;
      onChange(active);
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(check, 50);
  };

  let observer = null;
  if (typeof MutationObserver !== 'undefined' && doc.body) {
    observer = new MutationObserver(schedule);
    observer.observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'state', 'style'],
    });
  }

  const interval = setInterval(check, 750);
  check();

  return () => {
    clearInterval(interval);
    if (observer) observer.disconnect();
  };
};

/**
 * Report the document height to the host so it can size the iframe.
 * Returns a stop function.
 */
export const observeEmbedHeight = (onHeight, doc = document) => {
  let last = 0;
  const measure = () => {
    const el = doc.documentElement;
    const body = doc.body;
    // <html>.scrollHeight never drops below the viewport, so it would echo the
    // iframe's current height back and the frame could never shrink; the body
    // is content-sized in embed mode (portal-embed.less).
    const height = Math.ceil(
      body
        ? Math.max(body.scrollHeight, body.offsetHeight)
        : el
        ? el.scrollHeight
        : 0
    );
    if (height && height !== last) {
      last = height;
      onHeight(height);
    }
  };

  let observer = null;
  if (typeof ResizeObserver !== 'undefined' && doc.body) {
    observer = new ResizeObserver(measure);
    // Only the body. Observing <html> re-measures when the host shrinks the
    // iframe viewport, which is what the YouTube height loop fed on.
    observer.observe(doc.body);
  }

  const interval = setInterval(measure, 500);
  measure();

  return () => {
    clearInterval(interval);
    if (observer) observer.disconnect();
  };
};
